import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { publicLayoutRoute } from '@/ui/pages/public'
import { resolveLanguage } from '@/application/i18n/language'
import { useDocumentMeta } from '@/application/hooks/useDocumentMeta'
import { OG_IMAGE_URL } from '@/application/seo/site'
import { useLocalizedUrl } from '@/application/hooks/useLocalizedUrl'
import { LegalPageLayout } from '@/ui/pages/public/legal/LegalPageLayout'
import { formatLastUpdated } from '@/ui/pages/public/legal/lastUpdated'
import { Button } from '@/ui/components/common/Button'
import { useConsentStore } from '@/application/stores/consent.store'

export const cookiesRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'cookies',
  component: CookiesPolicyPage,
})

/**
 * Cookie policy: what the consent record is, the two categories used, their
 * purpose and duration, first- vs third-party distinction, and a button that
 * actually reopens the real preferences dialog from the consent store (not a
 * dead link).
 */
export function CookiesPolicyPage() {
  const { t, i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)
  const openPreferences = useConsentStore((state) => state.openPreferences)

  const pageUrl = useLocalizedUrl('/cookies')

  useDocumentMeta({
    title: t('seo.cookies_title'),
    description: t('seo.cookies_description'),
    imageUrl: OG_IMAGE_URL,
    url: pageUrl,
    type: 'website',
  })

  return (
    <LegalPageLayout title={t('cookies.page_title')} lastUpdatedLabel={formatLastUpdated(lang)}>
      <p>{t('cookies.page_intro')}</p>

      <section>
        <h2>{t('cookies.page_what_is_title')}</h2>
        <p>{t('cookies.page_what_is_body')}</p>
      </section>

      <section>
        <h2>{t('cookies.page_what_we_use_title')}</h2>
        <p>{t('cookies.page_categories_intro')}</p>
        <ul>
          <li>
            <strong>{t('cookies.category_necessary_title')}</strong> —{' '}
            {t('cookies.page_category_necessary')}
          </li>
          <li>
            <strong>{t('cookies.category_media_title')}</strong> —{' '}
            {t('cookies.page_category_media')}
          </li>
        </ul>
      </section>

      <section>
        <h2>{t('cookies.page_first_third_title')}</h2>
        <p>{t('cookies.page_first_third_body')}</p>
      </section>

      <section>
        <h2>{t('cookies.page_duration_title')}</h2>
        <p>{t('cookies.page_duration_body')}</p>
      </section>

      <section>
        <h2>{t('cookies.page_change_title')}</h2>
        <p>{t('cookies.page_change_body')}</p>
        <Button type="button" onClick={openPreferences} className="mt-2">
          {t('cookies.page_change_button')}
        </Button>
      </section>

      <section>
        <h2>{t('cookies.page_browser_title')}</h2>
        <p>{t('cookies.page_browser_body')}</p>
      </section>
    </LegalPageLayout>
  )
}
